import { TelemetryPanelConfig } from '../types/telemetry-dashboard.types';

export const TELEMETRY_GRID_COLS = 12;
export const TELEMETRY_GRID_ROW_H = 74;
export const TELEMETRY_GRID_GAP = 4;
export const TELEMETRY_GRID_PAD = 16;

export interface TelemetryGapZone {
  key: string;
  orient: 'v' | 'h';
  x?: number;
  y?: number;
  xStart?: number;
  xEnd?: number;
  yStart?: number;
  yEnd?: number;
  left: number;
  top: number;
  width: number;
  height: number;
  panelW?: number;
  panelH?: number;
  isLeft?: boolean;
  isSlot?: boolean;
}

export interface TelemetryEmptySlot {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

export type GapZone = TelemetryGapZone;

/** Snap pixel coordinates to discrete grid column & row */
export function snapTelemetryPanelToGrid(
  a: any,
  b: any,
  c?: any,
  d?: any,
  w?: any,
  h?: any
): { x: number; y: number } {
  if (typeof a === 'number') {
    const left = a;
    const top = b;
    const colW = c || 100;
    const x = Math.max(0, Math.min(TELEMETRY_GRID_COLS - 1, Math.round(left / (colW + TELEMETRY_GRID_GAP))));
    const y = Math.max(0, Math.round(top / (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP)));
    return { x, y };
  } else {
    const rawX = Math.round(c || 0);
    const rawY = Math.round(d || 0);
    const x = Math.max(0, Math.min(TELEMETRY_GRID_COLS - (w || 1), rawX));
    const y = Math.max(0, rawY);
    return { x, y };
  }
}

/** Check if two panel rectangles intersect in grid space */
export function isTelemetryPanelOverlapping(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Compute available gap zones between panels for insertion (left, right, top, bottom) */
export function findTelemetryGapZones(
  layout: TelemetryPanelConfig[] | any[],
  colW: number
): TelemetryGapZone[] {
  const zones: TelemetryGapZone[] = [];
  const HIT = TELEMETRY_GRID_GAP + 10;
  const OFF = (HIT - TELEMETRY_GRID_GAP) / 2;
  const pxX = (x: number) => x * (colW + TELEMETRY_GRID_GAP);
  const pxY = (y: number) => y * (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP);
  const pxW = (w: number) => w * colW + (w - 1) * TELEMETRY_GRID_GAP;
  const pxH = (h: number) => h * TELEMETRY_GRID_ROW_H + (h - 1) * TELEMETRY_GRID_GAP;

  const addedKeys = new Set<string>();

  for (const p of layout) {
    // 1. Bottom border gap zone
    const hKey = 'h-bot-' + p.id;
    if (!addedKeys.has(hKey)) {
      addedKeys.add(hKey);
      zones.push({
        key: hKey,
        orient: 'h',
        y: p.y + p.h,
        xStart: p.x,
        xEnd: p.x + p.w,
        left: pxX(p.x),
        top: pxY(p.y) + pxH(p.h) - OFF,
        width: pxW(p.w),
        height: HIT,
        panelW: p.w,
        panelH: p.h,
      });
    }

    // 2. Top border gap zone (if panel is not at the very top)
    if (p.y > 0) {
      const topKey = 'h-top-' + p.id;
      if (!addedKeys.has(topKey)) {
        addedKeys.add(topKey);
        zones.push({
          key: topKey,
          orient: 'h',
          y: Math.max(0, p.y - p.h),
          yStart: Math.max(0, p.y - p.h),
          xStart: p.x,
          xEnd: p.x + p.w,
          left: pxX(p.x),
          top: pxY(p.y) - OFF,
          width: pxW(p.w),
          height: HIT,
          panelW: p.w,
          panelH: p.h,
        });
      }
    }

    // 3. Right border gap zone
    if (p.x + p.w < TELEMETRY_GRID_COLS) {
      const vRightKey = 'v-right-' + p.id;
      if (!addedKeys.has(vRightKey)) {
        addedKeys.add(vRightKey);
        zones.push({
          key: vRightKey,
          orient: 'v',
          x: p.x + p.w,
          yStart: p.y,
          yEnd: p.y + p.h,
          left: pxX(p.x) + pxW(p.w) - OFF,
          top: pxY(p.y),
          width: HIT,
          height: pxH(p.h),
          panelW: p.w,
          panelH: p.h,
        });
      }
    }

    // 4. Left border gap zone (when panel is indented / moved right)
    if (p.x > 0) {
      const vLeftKey = 'v-left-' + p.id;
      if (!addedKeys.has(vLeftKey)) {
        addedKeys.add(vLeftKey);
        zones.push({
          key: vLeftKey,
          orient: 'v',
          isLeft: true,
          x: p.x,
          yStart: p.y,
          yEnd: p.y + p.h,
          left: pxX(p.x) - OFF,
          top: pxY(p.y),
          width: HIT,
          height: pxH(p.h),
          panelW: p.w,
          panelH: p.h,
        });
      }
    }
  }

  return zones;
}

/** Compute available empty rectangular slots within the active grid bounded by maxY */
export function findTelemetryEmptySlots(
  layout: TelemetryPanelConfig[] | any[],
  colW: number,
  minW: number = 1,
  minH: number = 1
): TelemetryEmptySlot[] {
  if (!layout || layout.length === 0) return [];

  const maxY = layout.reduce((m, p) => Math.max(m, p.y + p.h), 0);
  if (maxY <= 0) return [];

  const pxX = (x: number) => x * (colW + TELEMETRY_GRID_GAP);
  const pxY = (y: number) => y * (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP);
  const pxW = (w: number) => w * colW + (w - 1) * TELEMETRY_GRID_GAP;
  const pxH = (h: number) => h * TELEMETRY_GRID_ROW_H + (h - 1) * TELEMETRY_GRID_GAP;

  // Build occupancy matrix
  const occupied: boolean[][] = Array.from({ length: maxY }, () =>
    Array(TELEMETRY_GRID_COLS).fill(false)
  );

  for (const p of layout) {
    const startX = Math.max(0, Math.min(TELEMETRY_GRID_COLS - 1, p.x));
    const endX = Math.max(0, Math.min(TELEMETRY_GRID_COLS, p.x + p.w));
    const startY = Math.max(0, Math.min(maxY - 1, p.y));
    const endY = Math.max(0, Math.min(maxY, p.y + p.h));
    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        occupied[y][x] = true;
      }
    }
  }

  const visited: boolean[][] = Array.from({ length: maxY }, () =>
    Array(TELEMETRY_GRID_COLS).fill(false)
  );

  const slots: TelemetryEmptySlot[] = [];

  for (let y = 0; y < maxY; y++) {
    for (let x = 0; x < TELEMETRY_GRID_COLS; x++) {
      if (occupied[y][x] || visited[y][x]) continue;

      // Determine width of free block on row y
      let w = 0;
      while (x + w < TELEMETRY_GRID_COLS && !occupied[y][x + w] && !visited[y][x + w]) {
        w++;
      }

      // Determine height of free block downward across the exact horizontal span [x, x + w - 1]
      let h = 1;
      while (y + h < maxY) {
        let rowAllFree = true;
        for (let cx = x; cx < x + w; cx++) {
          if (occupied[y + h][cx] || visited[y + h][cx]) {
            rowAllFree = false;
            break;
          }
        }
        if (!rowAllFree) break;
        h++;
      }

      // Mark cells as visited
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          visited[y + dy][x + dx] = true;
        }
      }

      // Only produce slot if size meets minimum requirements
      if (w >= minW && h >= minH) {
        slots.push({
          key: `empty-slot-${x}-${y}-${w}-${h}`,
          x,
          y,
          w,
          h,
          left: pxX(x),
          top: pxY(y),
          width: pxW(w),
          height: pxH(h),
        });
      }
    }
  }

  return slots;
}

/** Check if two panels overlap. */
export const collide = (a: any, b: any): boolean =>
  a.id !== b.id && a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/** Check if a panel collides with any in a list. */
export const collideAny = (item: any, list: any[]): boolean =>
  list.some((b) => collide(item, b));

/** Compact layout upward, keeping one panel in place. */
export const compactExcept = (list: any[], keepId: string): any[] => {
  const keep = list.find((o) => o.id === keepId);
  const others = list
    .filter((o) => o.id !== keepId)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  const placed = keep ? [{ ...keep }] : [];
  for (const it of others) {
    let y = it.y;
    const clone = { ...it };
    while (y > 0 && !collideAny({ ...clone, y: y - 1 }, placed)) y--;
    clone.y = y;
    placed.push(clone);
  }
  return list.map((o) => placed.find((p) => p.id === o.id) || o);
};

/** Resolve all collisions by pushing blockers down, then compact. */
export const settleLayout = (layout: any[], keepId: string): any[] => {
  let l = layout.map((o) => ({ ...o }));
  let queue = [keepId];
  let guard = 0;
  while (queue.length && guard++ < 400) {
    const id = queue.shift()!;
    const cur = l.find((o) => o.id === id);
    if (!cur) continue;
    for (const other of l) {
      if (other.id === cur.id) continue;
      if (collide(cur, other)) {
        const newY = cur.y + cur.h;
        if (other.y < newY) {
          other.y = newY;
          queue.push(other.id);
        }
      }
    }
  }
  return compactExcept(l, keepId);
};

/** Move a widget to a new grid position. */
export const moveTo = (layout: any[], id: string, x: number, y: number): any[] => {
  let l = layout.map((o) => ({ ...o }));
  const m = l.find((o) => o.id === id);
  if (!m) return layout;
  m.x = Math.max(0, Math.min(x, TELEMETRY_GRID_COLS - m.w));
  m.y = Math.max(0, y);
  return settleLayout(l, id);
};

/** Resize a widget with position change (for drag handles). */
export const resizeRect = (layout: any[], id: string, x: number, y: number, w: number, h: number): any[] => {
  let l = layout.map((o) => ({ ...o }));
  const m = l.find((o) => o.id === id);
  if (!m) return layout;
  const minW = m.minW || 2, minH = m.minH || 2;
  w = Math.max(minW, w);
  h = Math.max(minH, h);
  x = Math.max(0, Math.min(x, TELEMETRY_GRID_COLS - w));
  y = Math.max(0, y);
  m.x = x; m.y = y; m.w = w; m.h = h;
  return settleLayout(l, id);
};

export { findTelemetryGapZones as gapZones };

/** Calculate panel wrapper CSS properties for grid vs list view */
export function getTelemetryPanelWrapperStyle(
  panel: { id: string; x: number; y: number; w: number; h: number; graphMode?: string },
  layoutCtx: {
    layoutMode: 'grid' | 'list';
    pxX: (x: number) => number;
    pxY: (y: number) => number;
    pxW: (w: number) => number;
    pxH: (h: number) => number;
    dragId: string | null;
    dragPos: { top: number; left: number } | null;
    resizeId: string | null;
    resizePos: { left: number; top: number; width: number; height: number } | null;
  }
): React.CSSProperties {
  const isDrag = layoutCtx.dragId === panel.id;
  const isResize = layoutCtx.resizeId === panel.id;
  const rp = isResize ? layoutCtx.resizePos : null;
  const dp = isDrag ? layoutCtx.dragPos : null;
  const isGrid = layoutCtx.layoutMode === 'grid';

  if (isGrid) {
    return {
      position: 'absolute',
      left: dp ? dp.left : rp ? rp.left : layoutCtx.pxX(panel.x),
      top: dp ? dp.top : rp ? rp.top : layoutCtx.pxY(panel.y),
      width: rp ? rp.width : layoutCtx.pxW(panel.w),
      height: rp ? rp.height : layoutCtx.pxH(panel.h),
      transition: isDrag || isResize
        ? 'none'
        : 'left .18s cubic-bezier(.2,.8,.2,1),top .18s cubic-bezier(.2,.8,.2,1),width .18s cubic-bezier(.2,.8,.2,1),height .18s cubic-bezier(.2,.8,.2,1)',
      zIndex: isDrag || isResize ? 50 : 2,
    };
  }

  return {
    position: 'relative',
    width: '100%',
    height: rp ? rp.height : (layoutCtx.pxH(panel.h) || (panel.graphMode === 'kpi' ? 140 : 280)),
    transform: dp ? `translateY(${dp.top - layoutCtx.pxY(panel.y)}px)` : undefined,
    transition: isDrag || isResize ? 'none' : 'height 0.15s ease',
    boxSizing: 'border-box',
    zIndex: isDrag ? 50 : 1,
  };
}
